<template>
    <div
        class="px-7 py-6 box-border w-full mt-4 shadow-[0_10px_20px_0px_rgba(0,0,0,0.03)] rounded relative overflow-hidden"
    >
        <div class="absolute w-1 h-full top-0 left-0 bg-primary-1" v-if="isComponentExpanded"></div>
        <div class="w-full flex items-center">
            <component :is="icon" />
            <h2 class="text-body-3 text-default-1 ml-4">{{ title }}</h2>
            <div class="text-body-7 text-default-1 bg-semitone-primary-1 py-0.5 px-1 ml-2 rounded-3xl">
                {{ totalItemsSelectedLabel }}
            </div>
            <div class="flex-1"></div>
            <img
                    src="../../../assets/icons/chevron-down.svg"
                    class="xxs:hidden md:flex cursor-pointer w-2.5 transition-all rotate-180"
                    :class="{'rotate-0' : isComponentExpanded}"
                    @click="toggleComponentVisibility"
                    alt=""
            />
        </div>
        <div class="w-full pl-8 box-border overflow-hidden transition-all" :class="[isComponentExpanded ? 'max-h-[1000px]' : 'max-h-[0px]']">
            <p class="mt-5 text-body-5 text-default-2" v-if="description.length > 0">{{ description }}</p>
            <div v-if="canEdit && !isDisabled"
                class="flex justify-between w-full py-4 mt-4" :class="[isDisabled ? 'pointer-events-none opacity-60' : '']">
                <Checkbox
                        name="assigned-items"
                        label="Select all"
                        :modelValue="areAllItemsSelected"
                        @update:modelValue="toggleSelectAllItems"
                />
                <span class="text-default-1 text-body-6 capitalize cursor-pointer" @click="toggleSelectAllItems(false)">Clear all</span>
            </div>
            <div class="flex w-full relative">
                <img src="../../../assets/icons/search.svg" alt="" class="absolute top-1/2 right-0 left-auto -translate-y-1/2" />
                <input @input="debouncedSearch" v-model="search" type="text"
                       class="!ring-0 !focus:shadow-none !shadow-none w-full border-t-0 border-l-0 border-r-0 border-interactive-9 text-body-6 text-default-1 placeholder:text-default-3 mt-3 mb-3 pr-6"
                       placeholder="Search"/>
            </div>
            <div v-if="!searchLoading && items.length > 0" class="w-full box-border mt-4 flex flex-col" :class="[isDisabled ? 'pointer-events-none opacity-60' : '']">
                <RecycleScroller
                        class="max-h-[300px] relative"
                        ref="recycleScroller"
                        :items="items"
                        :item-size="itemHeightInPx"
                        itemClass="w-full h-4.5 flex items-center box-border"
                        key-field="uid"
                >
                    <template v-slot="{ item }">
                        <AssignGroupsItem
                            :item="item"
                            :checked="isChecked(item)"
                            @checked="handleOnChecked"
                            @unchecked="handleOnUnchecked"
                            :disabled="item.name.toLowerCase() === 'all members'"
                        />
                    </template>
                    <template #after>
                        <InfiniteScrollObserver
                            v-if="doInfiniteLoading"
                            @intersect="checkInViewport"
                        ></InfiniteScrollObserver>
                    </template>
                </RecycleScroller>
            </div>
            <div class="flex flex-col" v-if="searchLoading">
                <div class="skeleton-loader w-1/2 h-8 rounded-3xl mt-2" v-for="i in 4" :key="i"></div>
            </div>
            <div class="flex" v-if="!searchLoading && items.length === 0">
                <p class="w-full box-border text-default-2 py-4 text-center text-body-6">No items found</p>
            </div>
        </div>
    </div>
</template>

<script setup>
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'
import {defineAsyncComponent, watch} from "vue";
import Checkbox from "@/components/core/Checkbox.vue";
import {computed, ref} from "vue";
import debounce from "lodash.debounce";
import InfiniteScrollObserver from "@/components/InfiniteScrollObserver.vue";
import AssignGroupsItem from "@/views/admin/partials/AssignItem.vue";

const emit = defineEmits(['loadMoreItems', 'search', 'change']);
const props = defineProps({
    items: {
        type: Array,
        default: () => ([]),
        required: true,
    },
    searchLoading: {
        type: Boolean,
        default: false,
        required: true,
    },
    infiniteLoading: {
        type: Boolean,
        default: false,
        required: true,
    },
    total: {
        type: Number,
        required: true,
    },
    title: {
        type: String,
        required: true,
    },
    description: {
        type: String,
        default: '',
    },
    icon: {
        required: true,
    },
    isDisabled: {
        type: Boolean,
        default: false
    },
    canEdit: {
        type: Boolean,
        default: false
    },
});
const RecycleScroller = defineAsyncComponent(() =>
    import('vue-virtual-scroller').then(module => module.RecycleScroller)
)

const itemHeightInPx = 36;
const areAllItemsSelected = ref(false);
const recycleScroller = ref(null)
const isComponentExpanded = ref(true);
const search = ref('');
const itemList = ref({
    included: [],
    excluded: [],
});

const toggleComponentVisibility = () => {
    isComponentExpanded.value = !isComponentExpanded.value;
}

const toggleSelectAllItems = (selectAll) => {
    areAllItemsSelected.value = selectAll;

    if (selectAll) {
        itemList.value.included = ['*'];
        itemList.value.excluded = [];
    } else {
        itemList.value.included = [];
        itemList.value.excluded = [];
    }

    emit('change', itemList.value);
}

const handleOnChecked = (itemUid) => {
    if (areAllItemsSelected.value) {
        itemList.value.excluded.splice(itemList.value.excluded.indexOf(itemUid), 1);
    } else {
        itemList.value.included.push(itemUid);
    }

    emit('change', itemList.value);
};

const handleOnUnchecked = (itemUid) => {
    if (areAllItemsSelected.value) {
        itemList.value.excluded.push(itemUid);
    } else {
        itemList.value.included.splice(itemList.value.included.indexOf(itemUid), 1);
    }

    emit('change', itemList.value);
};

const isChecked = (item) => {
    const uid = item.uid
    const name = item.name;

    if(props.isDisabled && !props.canEdit) {
        return true;
    }
    if(areAllItemsSelected.value) {
        return !itemList.value.excluded.includes(uid);
    }

    if(name.toLowerCase() === 'all members'){
        return true;
    }

    return itemList.value.included.includes(uid);
}

const checkInViewport = () => {
    if (!doInfiniteLoading.value) {
        return;
    }
    emit('loadMoreItems', search.value);
}

const debouncedSearch = debounce((event) => {
    emit('search', event.target.value);
}, 500);

const totalItemsSelectedLabel = computed(() => {
    if(props.isDisabled && !props.canEdit) {
        return 'All selected' + ' (' + props.total + ')';
    }
    if (areAllItemsSelected.value && itemList.value.excluded.length === 0) {
        return 'All selected';
    }

    if(areAllItemsSelected.value && itemList.value.excluded.length > 0) {
        return props.total - itemList.value.excluded.length;
    }

    if(props.items.find(item => item.name.toLowerCase() === 'all members')) {
        return itemList.value.included.length + 1;
    }

    return itemList.value.included.length;
});

const doInfiniteLoading = computed(() => {
    return props.items.length < props.total && !props.searchLoading && !props.infiniteLoading;
});

watch(props.items, () => {
    recycleScroller.value.updateVisibleItems(true)
}, { deep: true });
</script>
